import { DocumentPermission } from '@st/sdk'
import { ContextMenu, ContextMenuItem, RotateCCWIcon, RotateCWIcon, TrashIcon } from '@st/theme'

type Props = {
  page: { pageNumber: number }
  permissions: DocumentPermission[]
  isDeleted: boolean
  rotation: number
  onDeletePage?: (pageNumber: number) => void
  onRestorePage?: (pageNumber: number) => void
  onRotatePage?: (pageNumber: number, rotation: number) => void
}

/**
 * Toolbar displayed above each document page in the StanfordTax Document Viewer.
 * See {@link STPDFDocViewer} for more details.
 *
 * The toolbar shows the current page number and, when `showPageOperations` is enabled,
 * provides a context menu to delete/restore a page.
 */
export function STDocumentPageToolbar({
  page,
  rotation,
  permissions,
  isDeleted,
  onDeletePage,
  onRestorePage,
  onRotatePage
}: Props) {
  return (
    <div className="flex w-full items-center justify-between">
      <span className="mb-2 text-xs text-white">Page {page.pageNumber}</span>
      {permissions.includes('delete_pages') && (
        <ContextMenu
          className="mb-2 !size-4 text-white"
          showOnlyOnHover={false}
          aria-label={`Page ${page.pageNumber} menu`}
        >
          {!isDeleted ? (
            <ContextMenuItem
              icon={<TrashIcon className="size-4" />}
              onClick={() => onDeletePage?.(page.pageNumber)}
            >
              Delete page
            </ContextMenuItem>
          ) : (
            <ContextMenuItem
              icon={<TrashIcon className="size-4" />}
              onClick={() => onRestorePage?.(page.pageNumber)}
            >
              Restore page
            </ContextMenuItem>
          )}
          <ContextMenuItem
            icon={<RotateCWIcon className="size-4" />}
            onClick={() => onRotatePage?.(page.pageNumber, calculateRotation(rotation, 90))}
          >
            Rotate CW
          </ContextMenuItem>
          <ContextMenuItem
            icon={<RotateCCWIcon className="size-4" />}
            onClick={() => onRotatePage?.(page.pageNumber, calculateRotation(rotation, -90))}
          >
            Rotate CCW
          </ContextMenuItem>
        </ContextMenu>
      )}
    </div>
  )
}

/**
 * Calculate the new rotation after applying a delta.
 * Rotation will always be 0, 90, 180, or 270 degrees
 */
function calculateRotation(rotation: number, delta: -90 | 90) {
  // +360 to ensure rotation fits in the appropriate range
  return (rotation + delta + 360) % 360
}
